// Matematica di pan e zoom del canvas della formula.
//
// Sta qui fuori dal componente perche' e' la parte che si sbaglia in silenzio: un punto fermo
// scelto male non da' errori, sposta solo la vista dove non te l'aspetti.
//
// Il transform e' `translate(translateX, translateY) scale(scale)`: un punto p del mondo finisce
// sullo schermo in `translate + p * scale`.

export const SCALA_MIN = 0.1;
export const SCALA_MAX = 3;

/** Ritaglia una scala nell'intervallo consentito. */
export const limitaScala = (scala) => Math.max(SCALA_MIN, Math.min(SCALA_MAX, scala));

/**
 * Zoom che tiene fermo un punto dello schermo.
 *
 * Cambiare la sola scala tiene fermo il punto (0,0) del mondo, che a formula caricata sta quasi
 * sempre fuori schermo: il grafo scappa via. Qui il punto fermo lo scegli tu - il puntatore per
 * la rotella, il centro della vista per i pulsanti.
 *
 * @param {{scale:number, translateX:number, translateY:number}} t transform corrente
 * @param {number} fattore quanto moltiplicare la scala
 * @param {number} ancoraX x del punto fermo, in coordinate schermo relative al canvas
 * @param {number} ancoraY y del punto fermo
 */
export const zoomAncorato = (t, fattore, ancoraX, ancoraY) => {
  const nuovaScala = limitaScala(t.scale * fattore);
  if (nuovaScala === t.scale) return t;
  const k = nuovaScala / t.scale;
  return {
    scale: nuovaScala,
    translateX: ancoraX - (ancoraX - t.translateX) * k,
    translateY: ancoraY - (ancoraY - t.translateY) * k,
  };
};

/**
 * Il delta di una rotella in pixel.
 *
 * Firefox manda deltaMode 1 (righe, deltaY intorno a 3), Chrome deltaMode 0 (pixel, deltaY
 * intorno a 100). Senza normalizzare, lo stesso gesto fa cose diverse a seconda del browser.
 */
export const deltaInPixel = (e) => {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * 400;
  return e.deltaY;
};

/**
 * Quanto zoomare per un dato delta di rotella.
 *
 * Esponenziale e proporzionale al delta: un trackpad, che manda tanti eventi piccoli, diventa
 * continuo, e un mouse, che ne manda pochi e grossi, mantiene un passo netto per tacca. Prima
 * era `deltaY > 0 ? 0.9 : 1.1`, che ignorava l'ampiezza e trattava i due dispositivi uguali.
 */
export const fattoreZoomDaDelta = (deltaPixel) => Math.exp(-deltaPixel / 400);

/**
 * Come interpretare un evento di rotella.
 *
 * La rotella zooma sempre, ancorata al puntatore. Quello che cambia rispetto a prima e' che il
 * passo segue l'ampiezza del delta invece di essere un 10% fisso per evento: da mouse la tacca
 * resta netta, da trackpad lo zoom diventa continuo invece di saltare.
 */
export const interpretaRotella = (e) => ({
  tipo: 'zoom',
  fattore: fattoreZoomDaDelta(deltaInPixel(e)),
});

/** Applica uno scorrimento al transform. Usato dal pan, non dalla rotella. */
export const scorri = (t, dx, dy) => ({
  ...t,
  translateX: t.translateX - dx,
  translateY: t.translateY - dy,
});

/** Il rettangolo fra due punti, comunque li abbia trascinati. */
export const rettangoloFra = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  width: Math.abs(b.x - a.x),
  height: Math.abs(b.y - a.y),
});

/** I nodi che il rettangolo tocca, anche solo in parte. */
export const nodiNelRettangolo = (nodi, rect) => nodi.filter((n) =>
  !(n.x > rect.x + rect.width || n.x + n.width < rect.x ||
    n.y > rect.y + rect.height || n.y + n.height < rect.y));
